import React from 'react';
import {
  FaPlay,
  FaPowerOff,
  FaPause,
  FaQuestionCircle,
} from 'react-icons/fa';

/**
 * Mattoncini UI condivisi della dashboard ESXi. Puramente presentazionali,
 * tema chiaro/scuro via classi Tailwind `dark:`, focus visibile, e rispetto di
 * `prefers-reduced-motion` (varianti `motion-reduce:`). Coerenti con lo stile
 * già usato in SiSketron (card arrotondate, palette primary/gray).
 */

/** Icona per lo stato di alimentazione, mappata da una chiave simbolica. */
export function PowerIcon({ name, ...props }) {
  const map = {
    play: FaPlay,
    power: FaPowerOff,
    pause: FaPause,
    question: FaQuestionCircle,
  };
  const Icon = map[name] || FaQuestionCircle;
  return <Icon {...props} />;
}

export function Card({ children, className = '', as: Tag = 'section', ...rest }) {
  return (
    <Tag
      className={`rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function SectionTitle({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
            <Icon size={18} aria-hidden="true" />
          </span>
        )}
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

export function Badge({ children, className = '', title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

/** Pallino colorato + testo (non ci si affida al solo colore). */
export function StatusDot({ dotClass = 'bg-gray-400', label, title }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span
        className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`}
        aria-hidden="true"
      />
      {label && <span>{label}</span>}
    </span>
  );
}

/** Barra di utilizzo con etichetta accessibile. */
export function UsageBar({
  value, // 0..100 o null
  valueLabel,
  colorClass = 'bg-primary',
  label,
  className = '',
}) {
  const pct = value == null ? null : Math.max(0, Math.min(100, value));
  return (
    <div className={className}>
      {label && (
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">{label}</span>
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {valueLabel || (pct == null ? 'n/d' : `${pct}%`)}
          </span>
        </div>
      )}
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        aria-label={label || 'Utilizzo'}
        aria-valuenow={pct == null ? undefined : pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={pct == null ? 'non determinato' : `${pct}%`}
      >
        <div
          className={`h-full rounded-full transition-all duration-500 motion-reduce:transition-none ${
            pct == null ? 'bg-gray-300 dark:bg-gray-600' : colorClass
          }`}
          style={{ width: `${pct == null ? 100 : pct}%` }}
        />
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 py-10 text-center dark:border-gray-600">
      {Icon && <Icon className="text-gray-400 dark:text-gray-500" size={28} aria-hidden="true" />}
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
      {hint && <p className="max-w-md text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
      {action}
    </div>
  );
}

export function Spinner({ className = 'h-5 w-5' }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none ${className}`}
      aria-hidden="true"
    />
  );
}

/** Pulsante coerente con il tema, con varianti semantiche. */
export function Button({
  children,
  variant = 'primary',
  className = '',
  icon: Icon,
  ...rest
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50';
  const variants = {
    primary: 'bg-primary text-white shadow-sm hover:bg-primary/90',
    secondary:
      'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600',
    ghost:
      'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
    // Azione non distruttiva → blu.
    info: 'bg-sky-600 text-white shadow-sm hover:bg-sky-700',
    // Spegnimento controllato → arancione (non rosso: non è forzato).
    warning: 'bg-amber-600 text-white shadow-sm hover:bg-amber-700',
    danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700',
  };
  return (
    <button
      type="button"
      className={`${base} ${variants[variant] || variants.primary} ${className}`}
      {...rest}
    >
      {Icon && <Icon size={16} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** Coppia etichetta/valore per le griglie di dettaglio. */
export function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <dt className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-gray-700 dark:text-gray-200">
        {children == null || children === '' ? '—' : children}
      </dd>
    </div>
  );
}
